import { useCallback, useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import {
  dismissPromo,
  selectShouldShowEhrPromo,
  snoozePromo,
} from '@features/ehr-extension-promo';
import { useEntitlement } from '@features/entitlements';
import { useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useModal } from '@store/hooks/useModal';

export type EhrPromoTriggerAction = 'copy-all' | 'copy-selected' | 'copy-section';

/**
 * Hook to manage the EHR extension promo popup.
 *
 * Dismissal state is persisted via Redux persist (scoped storage).
 *
 * - User clicks X (close): permanently dismissed
 * - User clicks "Maybe later": snoozed for 1 week
 * - User clicks "Learn More": permanently dismissed + navigates to settings
 * - User has unlimited entitlement (purchased add-on): never shown
 */
export function useEhrExtensionPromo() {
  const { open } = useModal();
  const shouldShow = useAppSelector(selectShouldShowEhrPromo);
  const isGateEnabled = useFeatureGate('feature_ehr_extension_rollout');
  const { entitlement } = useEntitlement('ehr_note_copy');
  const hasUnlimited = entitlement?.quota === null || entitlement?.quota === undefined;
  const hasPaidAddon = !!entitlement && hasUnlimited;
  const pendingRef = useRef(false);

  const showPromo = useCallback(
    (triggerAction: EhrPromoTriggerAction) => {
      if (!isGateEnabled || !shouldShow || hasPaidAddon) return;
      if (pendingRef.current) return;

      pendingRef.current = true;
      setTimeout(() => {
        pendingRef.current = false;
        open(MODAL_TYPES.EHR_EXTENSION_PROMO, { triggerAction, maxWidth: 'md' });
      }, 600);
    },
    [isGateEnabled, shouldShow, hasPaidAddon, open],
  );

  const wouldShowPromo = isGateEnabled && shouldShow && !hasPaidAddon;

  return { showPromo, wouldShowPromo };
}

/** Hook to get promo dispatch actions (for use in modal) */
export function useEhrPromoActions() {
  const dispatch = useAppDispatch();

  return {
    dismiss: useCallback(() => dispatch(dismissPromo()), [dispatch]),
    snooze: useCallback(() => dispatch(snoozePromo()), [dispatch]),
  };
}
